import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { accountApi } from "@/lib/api";

const RED = "#E3000B";
const money = (n) => `$${Number(n || 0).toFixed(2)}`;
export const DRAFT_KEY = "o4b_draft_v1";

export function startCustomerLogin() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/account";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

/** One-time #session_id → o4b_session cookie exchange for sellers. */
export function CustomerAuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const done = useRef(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const m = /session_id=([^&]+)/.exec(location.hash || "");
    if (!m) { navigate("/account", { replace: true }); return; }
    accountApi.exchange(decodeURIComponent(m[1]))
      .then((user) => { window.history.replaceState(null, "", "/account"); navigate("/account", { replace: true, state: { user } }); })
      .catch((e) => setError(e.response?.data?.detail || "Login failed"));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return <div className="min-h-screen bg-white flex items-center justify-center font-bold text-gray-700" data-testid="account-callback">{error || "Signing you in…"}</div>;
}

const STATUS_LABEL = { new: "Received", contacted: "In touch", accepted: "Confirmed", shipped: "Shipped", paid: "Paid", declined: "Declined" };

/** Seller dashboard: current batch, past submissions, total received. */
export default function Account() {
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [checked, setChecked] = useState(!!location.state?.user);
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState(null);
  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) return;
    accountApi.me().then(setUser).catch(() => setUser(null)).finally(() => setChecked(true));
  }, []);
  useEffect(() => {
    if (!user) return;
    accountApi.submissions().then(setData).catch(() => setData({ rows: [], total_received: 0, paid_count: 0, pending_total: 0 }));
    accountApi.getDraft().then((d) => {
      const local = JSON.parse(localStorage.getItem(DRAFT_KEY) || "null");
      const pick = !d?.chips?.length ? local : (!local?.chips?.length || (d.updated_at && local.updated_at && d.updated_at > local.updated_at)) ? d : local;
      setDraft(pick?.chips?.length ? pick : null);
    }).catch(() => setDraft(JSON.parse(localStorage.getItem(DRAFT_KEY) || "null")));
  }, [user]);
  const logout = async () => { await accountApi.logout(); setUser(null); setData(null); };

  if (!checked) return <div className="min-h-screen bg-white" />;
  return (
    <div className="min-h-screen bg-white text-black" style={{ fontFamily: "IBM Plex Sans, system-ui, sans-serif" }} data-testid="account-page">
      <div className="h-2 w-full" style={{ background: `linear-gradient(90deg, ${RED} 0 25%, #FFD500 25% 50%, #0055BF 50% 75%, #00A850 75%)` }} />
      <header className="max-w-md mx-auto px-5 pt-5 flex items-center justify-between">
        <Link to="/"><img src="/brand/offers4bricks-logo@1000.webp" alt="OFFERS4BRICKS" className="h-11 w-auto" /></Link>
        {user ? <button onClick={logout} className="text-xs font-bold text-gray-500 underline" data-testid="account-logout">Sign out</button>
              : <Link to="/" className="text-xs font-bold text-gray-500 underline">Back</Link>}
      </header>
      <main className="max-w-md mx-auto px-5 pb-16">
        {!user ? (
          <div className="mt-10 text-center" data-testid="account-login">
            <h1 className="text-3xl font-black" style={{ fontFamily: "Chivo, system-ui, sans-serif" }}>Your seller account</h1>
            <p className="text-gray-600 mt-2">Save your batch as you go, pick it up on any device, and see every past submission and payout in one place.</p>
            <button onClick={startCustomerLogin} data-testid="account-google-login"
              className="mt-8 w-full h-14 rounded-2xl bg-black text-white font-black text-base flex items-center justify-center gap-2 active:scale-[.99]">
              <span className="font-black text-[#4285F4] bg-white rounded-full w-7 h-7 flex items-center justify-center">G</span> Continue with Google
            </button>
            <p className="text-[11px] text-gray-400 mt-4">No account needed to get an offer — this just keeps your progress safe.</p>
          </div>
        ) : (
          <>
            <div className="mt-6 flex items-center gap-3">
              {user.picture && <img src={user.picture} alt="" className="w-10 h-10 rounded-full" />}
              <div><div className="font-black" data-testid="account-name">{user.name}</div><div className="text-xs text-gray-500">{user.email}</div></div>
            </div>
            <div className="mt-6 grid grid-cols-2 gap-3">
              <div className="rounded-2xl p-4 text-white" style={{ background: "#00A850" }} data-testid="account-total-received">
                <div className="text-[10px] font-black tracking-widest opacity-80">TOTAL RECEIVED</div>
                <div className="text-3xl font-black" style={{ fontFamily: "Chivo, system-ui, sans-serif" }}>{money(data?.total_received)}</div>
                <div className="text-[11px] opacity-80">{data?.paid_count || 0} paid order{data?.paid_count === 1 ? "" : "s"}</div>
              </div>
              <div className="rounded-2xl p-4 bg-[#FFF9D6]" data-testid="account-pending">
                <div className="text-[10px] font-black tracking-widest text-gray-600">IN PROGRESS</div>
                <div className="text-3xl font-black" style={{ fontFamily: "Chivo, system-ui, sans-serif" }}>{money(data?.pending_total)}</div>
                <div className="text-[11px] text-gray-600">awaiting payout</div>
              </div>
            </div>

            <section className="mt-8" data-testid="account-current-batch">
              <h2 className="text-xs font-black tracking-widest text-gray-500">CURRENT BATCH</h2>
              {draft?.chips?.length ? (
                <div className="mt-2 rounded-2xl border-2 border-black p-4">
                  <div className="font-black">{draft.chips.length} set{draft.chips.length === 1 ? "" : "s"} saved</div>
                  <div className="text-xs text-gray-600 mt-1 break-words">{draft.chips.slice(0, 12).join(" · ")}{draft.chips.length > 12 ? " …" : ""}</div>
                  <Link to="/?resume=1" className="mt-3 block text-center h-12 leading-[48px] rounded-2xl text-white font-black" style={{ background: RED }} data-testid="account-resume-btn">Resume this batch →</Link>
                </div>
              ) : (
                <div className="mt-2 rounded-2xl bg-[#F6F6F3] p-4 text-sm text-gray-600">No batch in progress. <Link to="/" className="font-bold underline">Start one →</Link></div>
              )}
            </section>

            <section className="mt-8" data-testid="account-submissions">
              <h2 className="text-xs font-black tracking-widest text-gray-500">PAST SUBMISSIONS</h2>
              {!data ? null : data.rows.length === 0 ? (
                <div className="mt-2 text-sm text-gray-600">Nothing yet — your submissions will show here with their status and payout.</div>
              ) : data.rows.map(r => (
                <div key={r.request_id} className="mt-2 rounded-2xl border border-gray-200 p-4" data-testid={`account-sub-${r.request_id}`}>
                  <div className="flex items-center justify-between">
                    <div className="font-mono font-black text-sm">{r.order_no || r.request_id}</div>
                    <span className={`text-[10px] font-black tracking-widest px-2 py-1 rounded-full ${r.status === "paid" ? "bg-[#00A850] text-white" : r.status === "declined" ? "bg-gray-200 text-gray-600" : "bg-[#FFD500] text-black"}`}>{(STATUS_LABEL[r.status] || r.status).toUpperCase()}</span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">{new Date(r.created_at).toLocaleDateString()} · {r.units} set{r.units === 1 ? "" : "s"}{r.review_items?.length ? ` · ${r.review_items.length} under review` : ""}</div>
                  <div className="flex items-center justify-between mt-2">
                    <div className="text-xl font-black" style={{ fontFamily: "Chivo, system-ui, sans-serif" }}>{money(r.total)}</div>
                    {r.invoice_path && <a href={r.invoice_path} target="_blank" rel="noopener noreferrer" className="text-xs font-bold underline text-[#0055BF]">Invoice</a>}
                  </div>
                </div>
              ))}
            </section>
          </>
        )}
      </main>
    </div>
  );
}
